import { createAction, createReducer, isActionOf } from 'typesafe-actions'
import { combineReducers } from 'redux'
import { combineEpics } from 'redux-observable'
import { filter, map, mergeMap } from 'rxjs/operators'
import { createSelectAction, payloadReducer, statusReducer } from '../../utils/redux-utils'
import { createProjectFilterReducer, listFiltersToTaskQuery, ProjectFiltersState } from './project-list-filters'
import { getId, idToggle } from '../../utils/entity-utils'
import { RequestStatus } from '../../responses/response'
import { BooleanRecord, ID } from '../../types/UtilityTypes'
import { EMPTY, of } from 'rxjs'
import { pipe } from 'fp-ts/es6/pipeable'
import { projectActions } from '../data/project/project-actions'
import { taskActions } from '../data/task/task-actions'
import { RootEpic } from '../root'
import { setProp } from '../../utils/utils'
import { openTaskReducer } from './project-detail'
import { taskStatusFilters } from '../../components/pages/projects/ProjectList/ProjectFilters/filters'
import { SortDirection } from '../../constants/direction'
import { listStatusFilters } from '../../components/pages/projects/ProjectList/config'

export const actions = {
    openProjects: createSelectAction(
        '@PROJECT_LIST/OPEN_PROJECTS_SET',
        '@PROJECT_LIST/OPEN_PROJECTS_TOGGLE',
        '@PROJECT_LIST/OPEN_PROJECTS_TOGGLE_ALL',
    ),
    toggleObject: createAction('@PROJECT_LIST/TOGGLE_OBJECT')<ID>(),
    openTask: createAction('@PROJECT_LIST/OPEN_TASK')<{parentId: ID, taskId: ID}>(),
    updateFilters: createAction('@PROJECT_LIST/UPDATE_FILTERS')<Partial<ProjectFiltersState>>(),
    clearFilters: createAction('@PROJECT_LIST/CLEAR_FILTERS')(),
}

export { actions as projectListActions }

export interface ProjectListState {
    status: RequestStatus,
    objectStatuses: Record<ID, RequestStatus>,
    projects: ID[],
    total: number,
    openProjects: ID[],
    openObjects: BooleanRecord,
    openTasks: Record<ID, ID>,
    filters: ProjectFiltersState,
}

export const projectListReducer = combineReducers<ProjectListState>({
    status: statusReducer({
        request: projectActions.list.init,
        success: projectActions.list.success,
        failure: projectActions.list.failure
    }),
    objectStatuses: createReducer<Record<ID, RequestStatus>>({})
        .handleAction(taskActions.parents.request, (state, action) => ({
            ...state,
            [action.payload.objectId]: RequestStatus.FETCHING,
        }))
        .handleAction(taskActions.parents.success, (state, action) => ({
            ...state,
            [action.payload.objectId]: RequestStatus.SUCCESS,
        }))
        .handleAction(taskActions.parents.failure, (state, action) => ({
            ...state,
            [action.payload.objectId]: RequestStatus.FAILURE,
        })),
    projects: createReducer<ID[]>([])
        .handleAction(projectActions.list.success, (state, action) => {
            const projects = action.payload.projects.map(getId)
            return action.payload.offset ? state.concat(projects) : projects
        })
        .handleAction(projectActions.remove.success, (state, action) => state.filter(id => id != action.payload))
        .handleAction(projectActions.list.failure, () => []),
    openProjects: createReducer<ID[]>([])
        .handleAction(actions.openProjects.set, payloadReducer)
        .handleAction(actions.openProjects.toggle, (state, { payload }) => pipe(state, idToggle(payload))),
    openObjects: createReducer<BooleanRecord>({})
        .handleAction(actions.toggleObject, (state, { payload }) => ({
            ...state,
            [payload]: !state[payload],
        })),
    openTasks: openTaskReducer
        .handleAction(actions.openTask, (state, action) =>
            pipe(state, setProp(action.payload.parentId, action.payload.taskId))
        ),
    filters: createProjectFilterReducer({
        dropdowns: {},
        openedAt: [undefined, undefined],
        projectStatus: listStatusFilters,
        sortField: null,
        sortDirection: SortDirection.ASC,
        taskStatus: taskStatusFilters[0],
        search: '',
        tags: [],
        object_design_status: null,
        object_production_status: null
    }, {
        update: actions.updateFilters,
        clear: actions.clearFilters,
    }),
    total: createReducer(0)
        .handleAction(projectActions.list.success, (_, action) => action.payload.total)
        .handleAction(projectActions.list.failure, () => 0)
})

const refetchTasks: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(projectActions.list.init)),
    mergeMap(() => Object.entries(state$.value.pages.projectList.openObjects).filter(([, open]) => open).map(
        ([objectId]) => taskActions.parents.request({
            objectId,
            query: listFiltersToTaskQuery(state$.value.pages.projectList.filters)
        })
    )),
)

const toggleObject: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(actions.toggleObject)),
    mergeMap(({ payload }) => state$.value.pages.projectList.openObjects[payload] ?
        of(taskActions.parents.request({
            objectId: payload,
            query: listFiltersToTaskQuery(state$.value.pages.projectList.filters)
        })) :
        EMPTY
    ),
)

const toggleAll: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(actions.openProjects.toggleAll)),
    map(() => {
        const { projects, openProjects } = state$.value.pages.projectList
        return actions.openProjects.set(openProjects.length === projects.length? []: projects)
    })
)

export const projectListEpic = combineEpics(refetchTasks, toggleObject, toggleAll)
